import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  FileText,
  Download,
  Eye,
  CheckCircle,
  Clock,
  Users,
  DollarSign,
  Calendar,
  ArrowRight
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const PayrollBatchesPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mock data
  useEffect(() => {
    setTimeout(() => {
      const mockBatches = [
        {
          id: 'BATCH-2024-12',
          month: '2024-12',
          employeeCount: 5,
          totalAmount: 77300,
          status: 'approved',
          createdAt: '2024-12-01',
          approvedAt: '2024-12-02'
        },
        {
          id: 'BATCH-2024-11',
          month: '2024-11',
          employeeCount: 5,
          totalAmount: 75000,
          status: 'completed',
          createdAt: '2024-11-01',
          approvedAt: '2024-11-02'
        },
        {
          id: 'BATCH-2024-10',
          month: '2024-10',
          employeeCount: 4,
          totalAmount: 62000,
          status: 'completed',
          createdAt: '2024-10-01',
          approvedAt: '2024-10-02'
        }
      ];
      setBatches(mockBatches);
      setLoading(false);
    }, 500);
  }, []);

  const getStatusBadge = (status) => {
    const config = {
      draft: { label: 'مسودة', className: 'bg-gray-50 text-gray-700 border-gray-200', icon: FileText },
      approved: { label: 'معتمد', className: 'bg-blue-50 text-blue-700 border-blue-200', icon: CheckCircle },
      completed: { label: 'مكتمل', className: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle }
    };
    const c = config[status] || config.draft;
    const Icon = c.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border ${c.className}`}>
        <Icon className="w-3 h-3" />
        {c.label}
      </span>
    );
  };

  const handleExport = (batchId, format) => {
    alert(`تصدير المسير ${batchId} بصيغة ${format}`);
  };

  const handleApprove = (batchId) => {
    if (window.confirm('هل تريد اعتماد هذا المسير؟')) {
      alert(`تم اعتماد المسير ${batchId}`);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/finance/salaries')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#1E3A8A] mb-4 transition-colors"
          >
            <ArrowRight className="w-5 h-5" />
            <span className="font-semibold">العودة للرواتب</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black text-[#1E3A8A] mb-2 flex items-center gap-3">
                <FileText className="w-8 h-8" />
                <span>مسيرات الرواتب</span>
              </h1>
              <p className="text-gray-600">إدارة المسيرات الشهرية</p>
            </div>
            {hasPermission('finance.salary.edit') && (
              <button
                onClick={() => alert('إنشاء مسير جديد')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white rounded-lg font-bold hover:shadow-lg transition-all"
              >
                <Plus className="w-5 h-5" />
                <span>إنشاء مسير جديد</span>
              </button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">الشهر</th>
                <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">عدد الموظفين</th>
                <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">إجمالي المبلغ</th>
                <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الحالة</th>
                <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">تاريخ الإنشاء</th>
                <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الإجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {batches.map(batch => (
                <tr key={batch.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <span className="font-bold text-[#1E3A8A]">{batch.month}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-bold">
                      <Users className="w-4 h-4" />
                      {batch.employeeCount}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-black text-lg text-gray-800">{batch.totalAmount.toLocaleString()} ر.س</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {getStatusBadge(batch.status)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-700">{batch.createdAt}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => alert('عرض تفاصيل المسير')}
                        className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                        title="عرض"
                      >
                        <Eye className="w-4 h-4 text-blue-600" />
                      </button>
                      <button
                        onClick={() => handleExport(batch.id, 'Excel')}
                        className="p-2 hover:bg-green-50 rounded-lg transition-colors"
                        title="تصدير Excel"
                      >
                        <Download className="w-4 h-4 text-green-600" />
                      </button>
                      {batch.status === 'draft' && hasPermission('finance.salary.approve') && (
                        <button
                          onClick={() => handleApprove(batch.id)}
                          className="p-2 hover:bg-yellow-50 rounded-lg transition-colors"
                          title="اعتماد"
                        >
                          <CheckCircle className="w-4 h-4 text-yellow-600" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default PayrollBatchesPage;